import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import Marquee from '../Marquee';
import SpacedHeading from '../SpacedHeading';
import { process } from '../../mock';

gsap.registerPlugin(ScrollTrigger);

export default function Process() {
  const containerRef = useRef(null);

  useGSAP(() => {
    const mm = gsap.matchMedia();
    const cards = gsap.utils.toArray('.process-card');
    
    mm.add("(min-width: 768px)", () => {
      // Desktop: Sophisticated staggered layout
      const vOffsets = [0, 80, 60, 160];
      cards.forEach((card, i) => {
        const isLeftCol = i % 2 === 0;
        gsap.fromTo(card, 
          { 
            x: isLeftCol ? -200 : 200,
            y: vOffsets[i] + 150,
            opacity: 0,
            rotate: isLeftCol ? -8 : 8,
            scale: 0.9
          },
          {
            x: 0,
            y: vOffsets[i],
            opacity: 1,
            rotate: [-2, 1.5, -1.5, 2][i],
            scale: 1,
            scrollTrigger: {
              trigger: card,
              start: 'top bottom+=100',
              end: 'top center',
              scrub: 1.2,
              ease: "power2.out"
            }
          }
        );
      });
    });

    mm.add("(max-width: 767px)", () => {
      // Mobile: Clean stacked layout with side entry only
      cards.forEach((card, i) => {
        const isLeft = i % 2 === 0;
        gsap.fromTo(card, 
          { 
            x: isLeft ? -100 : 100,
            y: 40,
            opacity: 0,
            rotate: isLeft ? -3 : 3
          },
          {
            x: 0,
            y: 0,
            opacity: 1,
            rotate: 0,
            scrollTrigger: {
              trigger: card,
              start: 'top bottom-=20',
              end: 'top center+=50',
              scrub: 1,
            }
          }
        );
      });
    });
  }, { scope: containerRef });

  return (
    <section id="process" ref={containerRef} className="relative bg-[var(--bg)] pt-20 md:pt-28 pb-40 md:pb-64">
      <Marquee items={['processo', 'processo', 'processo', 'processo', 'processo']} speed={8} className="border-y border-[var(--border)]" />


      <div className="max-w-7xl mx-auto px-6 md:px-10 mt-20 md:mt-28">
        <SpacedHeading className="text-4xl md:text-7xl lg:text-8xl mb-20 md:mb-28">
          Processo de Criação
        </SpacedHeading>

        <div className="relative grid md:grid-cols-2 gap-10 md:gap-x-12 md:gap-y-40">
          {process.map((p, i) => (
            <ProcessCard key={p.num} p={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProcessCard({ p, index }) {
  return (
    <motion.div
      whileHover={{ rotate: 0, scale: 1.02, zIndex: 10 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className={`process-card relative rounded-[var(--card-radius)] p-8 md:p-10 min-h-[320px] md:min-h-[380px] border ${
        p.highlight
          ? 'bg-gradient-to-br from-[var(--accent)] to-[var(--accent-secondary)] border-[var(--accent)] shadow-[0_20px_80px_-20px_rgba(255,90,31,0.6)]'
          : 'bg-[var(--card)] border-[var(--border)] shadow-xl'
      }`}
    >

      <div className="flex items-start justify-between mb-auto">
        <span className={`text-xs tracking-[0.3em] ${p.highlight ? 'text-white/90' : 'text-white/50'}`}>
          {p.num}
        </span>
        <div className="flex gap-1.5">
          {[0, 1, 2, 3].map((d) => (
            <span
              key={d}
              className={`w-1.5 h-1.5 rounded-full ${
                d === index ? (p.highlight ? 'bg-white' : 'bg-[var(--accent)]') : p.highlight ? 'bg-white/40' : 'bg-white/20'
              }`}
            />

          ))}
        </div>
      </div>

      <h3 className={`font-display text-4xl md:text-6xl uppercase tracking-tight mt-10 ${p.highlight ? 'text-white' : 'text-white'}`}>
        {p.title}
      </h3>

      <p className={`mt-6 text-sm md:text-base leading-relaxed max-w-md ${p.highlight ? 'text-white/95' : 'text-white/70'}`}>
        {p.desc}
      </p>
    </motion.div>
  );
}


